import CLOSE from "@assets/quick-news/close.svg";
import TOOLTIP from "@assets/quick-news/qn_tooltip.svg";
import { QN_ANALYTICS_EVENT_BASE } from "@config/QuickNewsConstants";
import {
  loadLottieAnimation,
  type LottieConfig,
  type LottieInstance,
} from "@helper/lottieHelper";
import { useTablaCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTablaCustomEventAnalyticsCall";
import { type ReactElement, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export function QnHelpBtn(): ReactElement {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tablaCustomEvent = useTablaCustomEventAnalyticsCall();
  const canvasId = `qn-help-btn-lottie`;
  const lottieInstanceRef = useRef<LottieInstance | null>(null);

  const handleClick = (): void => {
    setIsTooltipOpen((prev) => !prev);

    tablaCustomEvent({
      ...QN_ANALYTICS_EVENT_BASE,
      clickAction: "click help",
    });
  };

  useEffect(() => {
    const isActive = { current: true };

    const config: LottieConfig = {
      animationPath: "quick-news/tooltip.lottie",
      canvasId,
      dimensions: {
        height: 183,
        minHeight: 183,
      },
    };

    const instance = loadLottieAnimation(config);
    if (isActive.current && instance) {
      lottieInstanceRef.current = instance;
    }

    return () => {
      isActive.current = false;
      if (lottieInstanceRef.current) {
        lottieInstanceRef.current.destroy();
        lottieInstanceRef.current = null;
      }
    };
  }, [canvasId]);

  const buttonClasses = twMerge(
    "inline-flex h-[40px] w-[40px] items-center justify-center rounded-full transition-colors",
    isTooltipOpen && "bg-white/90",
  );
  const imgClasses = isTooltipOpen ? "p-2" : "p-1";
  const tooltipClasses = twMerge(
    "pointer-events-none absolute bottom-10 right-0 z-20 h-[183px] w-[183px] translate-y-full opacity-0 transition-opacity duration-300",
    isTooltipOpen && "opacity-100",
  );

  return (
    <div className="absolute right-4 hidden md:right-8 lg:block">
      <div className="relative z-30 m-auto">
        <button
          type="button"
          aria-label="Help"
          onClick={handleClick}
          className={buttonClasses}
        >
          <img
            alt="info"
            src={isTooltipOpen ? CLOSE : TOOLTIP}
            width={40}
            height={24}
            className={imgClasses}
          />
        </button>
      </div>

      {/* Tooltip */}
      <div className={tooltipClasses}>
        <canvas
          data-testid="qn-help-popup"
          id={canvasId}
          className="w-[183px]"
        />
      </div>
    </div>
  );
}
